import React, { useContext } from 'react';
import ReactDOM from 'react-dom';
import { useFlags } from 'wsm-common-data';
import { setClassNames } from 'ddc-classnames-js';
import { LoadingBox } from 'wsm-ai-loader';
import useFocusTrap from '../hooks/useFocusTrap';
import InventoryTabs from './InventoryTabs';
import RecentSearches from './RecentSearches';
import shouldShowRecentSearches from '../util/shouldShowRecentSearches';
import hasEmptyResults from '../util/hasEmptyResults';
import hasResults from '../util/hasResults';
import useAriaLive from '../hooks/useAriaLive';
import isEnoughAddData from '../util/isEnoughAddData';
import { SearchContext, UIContext, ResultsContext } from '../contexts';
import InventoryStandard from './InventoryStandard';

const DropDown = () => {
	const {
		searchTerm,
		numberOfCharactersForFirstSearchNumber,
		fetchingResults,
		isNLSFetchingResults
	} = useContext(SearchContext);

	const {
		dropDownRef,
		dropDownPosition,
		inputRef,
		inputElementRef,
		inputWidth,
		labels,
		modalIsOpen,
		closeModal
	} = useContext(UIContext);

	const {
		preparedInventoryResults,
		pageSearchResults,
		richContentToDisplay,
		recentSearches,
		handleLinkClick,
		addInfo,
		showTabs,
		hasSingleVehicle
	} = useContext(ResultsContext);

	const flags = useFlags();
	const naturalLanguageSearchUI = flags['natural-language-search-ui'];

	const { suggestions, vehicles, allVehiclesPath } =
		preparedInventoryResults || {};

	const isSearchTermLongEnough =
		searchTerm?.length >= numberOfCharactersForFirstSearchNumber;

	const hasResultsLocal = hasResults(
		pageSearchResults,
		suggestions,
		vehicles
	);

	const hasEmptyResultsLocal = hasEmptyResults(
		pageSearchResults,
		suggestions,
		vehicles
	);

	const announceText = useAriaLive(
		isSearchTermLongEnough,
		hasResultsLocal,
		hasEmptyResultsLocal
	);

	const hasRichContentToDisplay = isEnoughAddData(
		richContentToDisplay,
		addInfo
	);

	const shouldRenderNoResults =
		!hasResultsLocal &&
		hasEmptyResultsLocal &&
		!hasRichContentToDisplay &&
		!fetchingResults;

	useFocusTrap(
		[inputRef, dropDownRef],
		dropDownRef,
		null,
		inputElementRef,
		null,
		modalIsOpen,
		closeModal
	);

	const classNames = [
		'sts-search-dropdown',
		'ddc-box-1',
		'bg-contrast-low',
		'p-4',
		!modalIsOpen && 'hidden',
		'shadow'
	];

	if (
		!recentSearches?.length &&
		(!searchTerm ||
			!isSearchTermLongEnough ||
			(pageSearchResults === undefined &&
				suggestions === undefined &&
				vehicles === undefined))
	) {
		return null;
	}
	const viewLinkLabel = labels.get('VIEW_ALL_VEHICLES_SITE_SEARCH');
	const handleClick = () => {
		handleLinkClick('allInventoryLink', viewLinkLabel, '');
	};

	const showNLSLoading =
		!fetchingResults && isNLSFetchingResults && naturalLanguageSearchUI;
	const showFlyoutContentClassic =
		isSearchTermLongEnough && !naturalLanguageSearchUI;
	const showFlyoutContentNLS =
		naturalLanguageSearchUI &&
		isSearchTermLongEnough &&
		!isNLSFetchingResults;

	const dropDownContent = (
		<div
			ref={dropDownRef}
			className={setClassNames(classNames)}
			data-testid="sts-input-dropdown"
			data-widget-name="ws-site-text-search"
			role="dialog"
			aria-labelledby={
				searchTerm ? 'results-heading' : 'recent-searches-heading'
			}
			aria-modal="true"
			style={{
				position: 'absolute',
				top: dropDownPosition.top,
				left: dropDownPosition.left,
				width: inputWidth,
				minHeight: '117px'
			}}
		>
			<div aria-live="assertive" className="sr-only">
				{announceText}
			</div>

			{shouldShowRecentSearches(
				searchTerm,
				hasResultsLocal,
				hasEmptyResultsLocal,
				recentSearches
			) && <RecentSearches />}

			{showNLSLoading && (
				<LoadingBox vectorName="sparkleTwo" isGradient />
			)}

			{(showFlyoutContentClassic || showFlyoutContentNLS) && (
				<div>
					<h2 id="results-heading" className="sr-only">
						{labels.get('SEARCH_RESULTS')}
					</h2>
					{showTabs ? (
						<InventoryTabs
							hasRichContentToDisplay={hasRichContentToDisplay}
							shouldRenderNoResults={shouldRenderNoResults}
						/>
					) : (
						<>
							{allVehiclesPath &&
								!shouldRenderNoResults &&
								!hasSingleVehicle && (
									<a
										onClick={handleClick}
										href={allVehiclesPath}
										className="font-weight-bold py-3 px-4"
									>
										<span>
											{viewLinkLabel}
											<i
												className="ddc-icon ddc-icon-arrow-details ml-3"
												aria-hidden="true"
											/>
										</span>
									</a>
								)}
							<div className="row">
								<InventoryStandard
									vehicles={vehicles}
									suggestions={suggestions}
									shouldRenderNoResults={
										shouldRenderNoResults
									}
									hasRichContentToDisplay={
										hasRichContentToDisplay
									}
								/>
							</div>
						</>
					)}
				</div>
			)}
		</div>
	);
	return ReactDOM.createPortal(dropDownContent, document.body);
};

export default DropDown;
